import { useCentralStore } from "../../store/Central";
import { useDesktopsStore } from "../../store/Desktops";
import { showNotification } from "../../store/Central/selectors";
import { sendPrintJob, pollPrintResponse } from "./print-utils";
import { Logger } from "@/lib/logger/Logger";
import { isPrintAllowedForCurrentUser } from "./support-print-grant";

export interface PrintRequest {
	documentType: string;
	documentData: Blob;
	fileName: string;
}

export type PrintMode = "browser" | "desktop";

export interface DispatchResult {
	mode: PrintMode;
	success: boolean;
	desktopOffline?: boolean;
}

function resolveLocationId(): number | null {
	const { profile } = useCentralStore.getState();
	const { locations } = useDesktopsStore.getState();
	return (
		profile?.location_id ?? locations.find((l) => l.is_default)?.id ?? null
	);
}

function resolvePrintMode(): PrintMode {
	const locationId = resolveLocationId();
	if (!locationId) return "browser";
	return useDesktopsStore.getState().getOrgPrintMode(locationId);
}

function showDesktopHintOnce() {
	const key = "dentlab_print_hint_shown";
	if (sessionStorage.getItem(key)) return;
	sessionStorage.setItem(key, "1");
	showNotification({
		message:
			"Tipp: Mit der dentDESK Desktop-App drucken Sie direkt ohne Druckdialog.",
		type: "info",
	});
}

function showSupportPrintBlockedHint() {
	showNotification({
		message:
			"Druck am Desktop-Drucker unterdrückt (Support-Konto). Über 'Drucker freigeben' im Entwickler-Panel kann der Druck für 60s erlaubt werden.",
		type: "warning",
	});
}

function openBlobInNewTab(blob: Blob) {
	const url = URL.createObjectURL(blob);
	const win = window.open(url, "_blank");
	if (win) {
		win.focus();
		win.print();
	} else {
		showNotification({
			message: "Pop-up wurde blockiert. Bitte Pop-ups erlauben.",
			type: "warning",
		});
	}
	setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function dispatchPrint(
	request: PrintRequest
): Promise<DispatchResult> {
	const mode = resolvePrintMode();

	if (mode === "desktop" && !isPrintAllowedForCurrentUser()) {
		showSupportPrintBlockedHint();
		openBlobInNewTab(request.documentData);
		return { mode: "browser", success: true };
	}

	if (mode === "browser") {
		showDesktopHintOnce();
		openBlobInNewTab(request.documentData);
		return { mode: "browser", success: true };
	}

	const result = await sendPrintJob(request);

	if (!result.success) {
		return { mode: "desktop", success: false };
	}

	if (result.desktopOnline) {
		showNotification({ message: "Druckauftrag gesendet", type: "info" });
		if (result.requestId) {
			pollPrintResponse(result.requestId);
		}
		return { mode: "desktop", success: true };
	}

	return { mode: "desktop", success: true, desktopOffline: true };
}

export function getResolvedPrintMode(): PrintMode {
	return resolvePrintMode();
}

export function isAutoPrintEnabled(documentType: string): boolean {
	if (!isPrintAllowedForCurrentUser()) return false;
	const locationId = resolveLocationId();
	if (!locationId) return false;
	const mode = useDesktopsStore.getState().getOrgPrintMode(locationId);
	if (mode !== "desktop") return false;
	return useDesktopsStore.getState().getAutoprint(locationId, documentType);
}
